import { useCallback, useRef, useState } from 'react'

import i18n from 'i18n-literally'

import { styled } from '@mui/material/styles'

const Inputs = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
}))

const Digit = styled('input', { shouldForwardProp: prop => prop !== 'hasError' })<{ hasError?: boolean }>(
  ({ hasError, theme }) => ({
    appearance: 'textfield',
    border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    color: hasError ? theme.palette.error.main : theme.palette.text.primary,
    fontSize: '1.5rem',
    height: '3.5rem',
    textAlign: 'center',
    width: '3rem',
    '&:focus': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
      outline: 'none',
    },
  }),
)

export interface CodeInputProps {
  readonly error?: boolean
  readonly length?: number
  readonly onComplete?: (code: string) => void
}

/** A row of single-character boxes for a verification code. */
const CodeInput = ({ error = false, length = 6, onComplete }: CodeInputProps) => {
  const [digits, setDigits] = useState<string[]>(() => Array<string>(length).fill(''))
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  const commit = useCallback(
    (next: string[]) => {
      setDigits(next)
      const code = next.join('')
      if (code.length === length) onComplete?.(code)
    },
    [length, onComplete],
  )

  const handleChange = useCallback(
    (index: number, rawValue: string) => {
      const entered = rawValue.replace(/\D/g, '')
      if (!entered) {
        commit(digits.map((digit, position) => (position === index ? '' : digit)))
        return
      }

      const next = [...digits]
      entered
        .split('')
        .slice(0, length - index)
        .forEach((digit, offset) => {
          next[index + offset] = digit
        })
      commit(next)
      focusInput(Math.min(index + entered.length, length - 1))
    },
    [commit, digits, focusInput, length],
  )

  const handleKeyDown = useCallback(
    (index: number, key: string) => {
      if (key === 'Backspace' && !digits[index] && index > 0) focusInput(index - 1)
      if (key === 'ArrowLeft' && index > 0) focusInput(index - 1)
      if (key === 'ArrowRight' && index < length - 1) focusInput(index + 1)
    },
    [digits, focusInput, length],
  )

  return (
    <Inputs>
      {digits.map((digit, index) => (
        <Digit
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={element => {
            inputRefs.current[index] = element
          }}
          aria-label={`${i18n`Verification code digit`} ${index + 1}`}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={index === 0}
          data-testid={`code-digit-${index}`}
          hasError={error}
          inputMode="numeric"
          maxLength={length}
          value={digit}
          onChange={event => handleChange(index, event.target.value)}
          onKeyDown={event => handleKeyDown(index, event.key)}
        />
      ))}
    </Inputs>
  )
}

export default CodeInput
